/**
 * CreateUserDialog — 4-step wizard to create or invite a new user.
 *
 * Step 1: Basic Info         — name, email, employeeId, phone, employment type,
 *                              department, position, manager, start/end dates
 * Step 2: Clearance & Class. — clearance level, US Person verification fields,
 *                              classification access, export control access,
 *                              HIPAA workforce, PHI access, training requirements
 * Step 3: Access              — direct roles, group memberships
 * Step 4: Invitation          — send now / generate link
 *
 * Mock mode: appends to local state. In production: POST /api/v1/users.
 */

import * as React from 'react';
import { useState, useMemo, useEffect } from 'react';
import {
  ShieldCheck, UserPlus, Check, ChevronRight, ChevronLeft,
  Send, Link2, AlertTriangle, User as UserIcon,
  UsersRound,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/api-error';
import { toast } from 'sonner';
import {
  ROLES as MOCK_ROLES, GROUPS as MOCK_GROUPS,
  clearanceRequiresUsPerson,
  CLEARANCE_LABELS,
  type ClearanceLevel, type EmploymentType, type UsPersonDocumentType,
} from '@/lib/mock-users-data';
import { useIdentityFieldPolicy } from '@/lib/use-identity-field-policy';
// B2c-T2 — the employment-type options come from the server, not from a list
// in this file. See `use-user-field-options.ts` for what that replaced.
import { useUserFieldOptions } from '@/lib/use-user-field-options';
import { FieldOptionsNotice } from './field-options-notice';
import {
  useCreateUser,
  useRoles,
  useUserGroups,
  useAllDepartments,
  useUsers,
  // B2b-T3 — the ONE rendering of a person's departments, shared with the
  // roster cell and the detail page.
  formatUserDepartments,
  type CreateUserBody,
} from '@/lib/use-users';
// B2b-T3 — the ONE departments control, shared with the edit dialog.
import {
  DepartmentScopePicker,
  isCompleteScope,
  type DepartmentScopeValue,
} from './department-scope-picker';
import { todayLocalIso, formatDateOnly, normalizeDateOnly } from '@/lib/format';
import { DateInput } from './date-input';

type Step = 'basic' | 'clearance' | 'access' | 'invitation';

const ALL_STEPS: Array<{ id: Step; label: string; icon: any; description: string }> = [
  { id: 'basic',       label: 'Basic Info',             icon: UserIcon,    description: 'Identity and employment' },
  { id: 'clearance',   label: 'Clearance & Class.',     icon: ShieldCheck, description: 'Access tier and verification' },
  { id: 'access',      label: 'Access',                 icon: UsersRound,  description: 'Roles and groups' },
  { id: 'invitation',  label: 'Invitation',             icon: Send,        description: 'How to onboard' },
];

/**
 * The Clearance step is always present in the wizard. When no compliance
 * framework is active for the tenant, the step renders with a banner
 * explaining that clearance / US-Person / HIPAA fields are not in scope
 * — but the defaults the wizard records (`unclassified`, unverified,
 * `none`) are still made visible to the admin so they aren't surprised
 * to see those values on the review screen. Hiding the step entirely
 * caused the QA-found bug "review shows clearance/US-Person fields as
 * fixed defaults that cannot be configured" — by always showing the
 * step, the defaults become explicit instead of silent.
 */
function buildSteps(_showClearance: boolean): typeof ALL_STEPS {
  return ALL_STEPS;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (userId: string) => void;
}

// ─── Form state ─────────────────────────────────────────────────────────────

export interface FormState {
  // Step 1
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string;
  phoneNumber: string;
  /**
   * B2c-T2 — NULL until the admin picks one, and the value IS the stored
   * value. It was `EmploymentType` with a hardcoded `'fte'` default and a map
   * that turned it into something else on the way out.
   */
  employmentType: EmploymentType | null;
  // B2b-T3 — the SET, not one id. Same field, same step; a person belongs to
  // one or more departments or to all of them.
  departments: DepartmentScopeValue;
  position: string;
  managerId: string;
  startDate: string;
  endDate: string;
  // Cost center — closes #183. Optional free-text HR identifier;
  // backend column is TEXT, no length cap below the body schema's
  // 100-char max.
  costCenter: string;

  // Step 2
  clearance: ClearanceLevel;
  usPersonStatus: 'unknown' | 'yes' | 'no';
  usPersonDocType: UsPersonDocumentType;
  usPersonVerifyingOfficer: string;
  // USR-F1-T1 — no classification-tier, export-control or PHI-access-level
  // field: POST /users stores none of them (classification / export access
  // are DERIVED from clearance + US-person standing by the one clearance
  // rule; PHI access level has no column). A control whose value reaches no
  // store is a control that lies (rails/users.md cause 5).
  hipaaWorkforceMember: boolean;
  requiredTraining: string[];

  // Step 3
  roleIds: string[];
  groupIds: string[];

  // Step 4
  inviteMode: 'send_now' | 'generate_link';
}

// NB: startDate default uses `todayLocalIso()` (browser-local calendar
// day) NOT `new Date().toISOString().slice(0, 10)` (UTC calendar day).
// Closes #250 Failure A — in any tz west of UTC, the UTC calendar day
// is one day ahead of the user's local day during the late evening,
// which made the form open with tomorrow's date pre-filled. The
// admin's typed value would have to override the wrong default; if
// they didn't, the wrong date got persisted and surfaced as a "+1 day"
// shift on reload. See `format.ts > todayLocalIso()`.
//
// emptyState() is a factory (not a const) so the default Start Date
// reflects today's local day each time the wizard opens, not the local
// day at module-load time — otherwise a long-lived browser tab would
// keep yesterday's default forever.
const emptyState = (): FormState => ({
  firstName: '', lastName: '', email: '', employeeId: '', phoneNumber: '',
  employmentType: null, departments: { all: false, ids: [] }, position: '', managerId: '',
  startDate: todayLocalIso(), endDate: '',
  costCenter: '',
  clearance: 'unclassified',
  usPersonStatus: 'unknown', usPersonDocType: 'us_passport', usPersonVerifyingOfficer: '',
  hipaaWorkforceMember: false,
  requiredTraining: ['argus-platform-101'],
  // B1-T4: the wizard selects a role by its KEY — the stable machine name
  // every row of `GET /users/roles` now carries (principle #5). It used to be
  // the sentinel id `system:member`, which the API synthesised in code; those
  // ids are gone with the synthesis, and a UUID here would have snapped
  // silently to `viewer` in `mockRoleIdsToApiRole`.
  roleIds: ['member'], groupIds: [],
  inviteMode: 'send_now',
});

// ─── Selected role → API-role mapping ───────────────────────────────────────
//
// The selector works in role KEYS (`admin`, `member`, `viewer`,
// `power_user`) — what every row of `GET /users/roles` carries since B1-T4.
// The legacy mock shape (`role-admin`) and the retired sentinel shape
// (`system:admin`) are still ACCEPTED, because form state can be seeded by an
// older caller, but nothing emits them any more.
//
// The API's POST /users body accepts only the lean `viewer | member | admin`
// enum. Map by descending privilege: pick the highest-priv selected role and
// snap to the closest API role.
export function mockRoleIdsToApiRole(
  ids: string[]
): 'viewer' | 'member' | 'admin' {
  const has = (suffix: string) =>
    ids.includes(suffix) ||
    ids.includes(`role-${suffix}`) ||
    ids.includes(`system:${suffix}`);
  if (has('admin') || has('owner')) return 'admin';
  // 'power_user' is a mock-only intermediate that doesn't exist in the
  // v1 API role enum (see W1a-27 §reconciliation). Snap down to
  // 'member' until the role is added in v1.1+.
  if (has('power_user')) return 'member';
  if (has('member')) return 'member';
  return 'viewer';
}

// ─── Pure body builder (testable) ──────────────────────────────────────────
//
// Lifted out of the component so the round-8 #250 / role-filter
// regression chain can be locked with vitest unit tests that don't
// need a React tree. The body's date fields go through
// `normalizeDateOnly` so any shape drift between FormState (always
// YYYY-MM-DD by construction) and the wire (server expects YYYY-MM-DD)
// is canonicalised here before the JSON serialiser ever runs.
//
// `framework` is the pre-resolved visibility decisions from the
// `useIdentityFieldPolicy` hook — passing the booleans (not the hook
// result) keeps this function pure and trivially testable.
export function buildCreateUserBodyFromForm(
  form: FormState,
  framework: {
    clearanceVisible: boolean;
    usPersonVisible: boolean;
    hipaaVisible: boolean;
  }
): CreateUserBody {
  const body: CreateUserBody = {
    email: form.email.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    role: mockRoleIdsToApiRole(form.roleIds),
    inviteMode: form.inviteMode === 'send_now' ? 'send_now' : 'manual_link',
  };
  if (form.employeeId.trim()) body.employeeId = form.employeeId.trim();
  if (form.phoneNumber.trim()) body.phoneNumber = form.phoneNumber.trim();

  // ── Employment fields (bA1 migration) ─────────────────────────────
  // Wizard captures all of these in Step 1; persist them now.
  //
  // Closes #72: defensive UUID guard — non-UUID ids (the retired
  // MOCK_DEPARTMENTS shapes, `user-sarah-chen` for a manager) would
  // reach the server as a 422 VALIDATION_ERROR that the dialog used
  // to swallow as "Validation failed" without context.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  // B2b-T3 review B2bT3-RV-02 / BF-B2bT3-04 — THE SCOPE THE ADMIN PICKED IS
  // SENT, WHOLE, WHATEVER IT LOOKS LIKE.
  //
  // It used to drop every id the UUID guard did not like and then, if nothing
  // survived, OMIT the `departments` key — which the server reads as
  // decisions.md C7's ALL-DEPARTMENTS person. An admin who deliberately
  // narrowed somebody to one department got the widest scope on the rail, with
  // no error, no toast and (before BF-B2bT3-05) nothing in the audit row. That
  // is the fail-open direction principles.md #4 names, and it is the inverse of
  // what the server's `{ all: false, ids: [] }` refusal exists to say.
  //
  // The key is sent exactly as picked now. A department the picker could not
  // resolve to a real id reaches the server as a 422 — a REFUSAL the operator
  // sees — rather than as a silent widening. (The mock fallback that produced
  // such ids is gone from the picker; the guard stays as the belt.)
  body.departments = form.departments.all
    ? { all: true, ids: [] }
    : { all: false, ids: [...form.departments.ids] };
  if (form.position.trim()) body.position = form.position.trim();
  if (form.managerId && UUID_RE.test(form.managerId)) {
    body.managerId = form.managerId;
  }
  // Closes #250 round 8: dates flow through normalizeDateOnly so any
  // accidental shape drift is canonicalised before serialisation.
  // The DateInput's onBlur defence already guarantees the form state
  // is YYYY-MM-DD at submit time; this is a second belt-and-braces
  // layer in case a future call site introduces a different shape.
  const startDate = normalizeDateOnly(form.startDate);
  if (startDate) body.startDate = startDate;
  const endDate = normalizeDateOnly(form.endDate);
  if (endDate) body.endDate = endDate;
  if (form.costCenter.trim()) body.costCenter = form.costCenter.trim();
  // B2c-T2 — SENT AS IT WAS PICKED. The `employmentTypeMap` that stood here
  // translated the browser's four options into the store's four, and it was
  // not injective: "Vendor" was stored as `contractor` and "Partner" as
  // `intern`, so the value the admin chose was not the value the system held.
  // c32 made the two alphabets one list and GET /users/field-options hands it
  // to this form, so the option's value is the stored value. Omitted when the
  // admin has not picked one — the column is nullable and a guess is not an
  // answer.
  if (form.employmentType) body.employmentType = form.employmentType;

  // Identity fields — gated by the framework policy. Only include keys
  // the active frameworks visibly accept.
  if (framework.clearanceVisible) {
    body.clearance = form.clearance;
  }
  if (framework.usPersonVisible) {
    // 'yes' → verified=true with timestamp; 'no' → verified=false;
    // 'unknown' → don't send (let the row default to false + null).
    if (form.usPersonStatus === 'yes') {
      body.usPersonVerified = true;
      body.usPersonVerifiedAt = new Date().toISOString();
    } else if (form.usPersonStatus === 'no') {
      body.usPersonVerified = false;
      body.usPersonVerifiedAt = null;
    }
  }
  if (framework.hipaaVisible && form.hipaaWorkforceMember) {
    body.hipaaBaaStatus = 'pending'; // signed/expired flow lands in W1a-04
  }
  return body;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function CreateUserDialog({ open, onOpenChange, onCreated }: CreateUserDialogProps) {
  const [step, setStep] = useState<Step>('basic');
  // Lazy initial state — `emptyState()` recomputes the local-day
  // Start Date default when the component mounts so a long-lived
  // browser tab doesn't keep yesterday's default. See #250.
  const [form, setForm] = useState<FormState>(() => emptyState());
  const [serverError, setServerError] = useState<{
    code: string;
    message: string;
  } | null>(null);

  const createUser = useCreateUser();

  // Framework-driven step list. Without an active compliance framework, the
  // Clearance step is skipped entirely (3 steps, not 4). Phase B Step 2.5.
  const { policy } = useIdentityFieldPolicy();
  const STEPS = useMemo(() => buildSteps(policy.hasAny), [policy.hasAny]);

  // B2c-T2 — the employment-type options, and the one rule that varies per
  // option, come from the server (GET /users/field-options). `canProceed`
  // used to ask `form.employmentType === 'fte'`, re-deriving from a key a
  // rule the field itself knows.
  const { options: fieldOptions } = useUserFieldOptions();

  // Recover from stale step state if policy changes mid-wizard (e.g., admin
  // disables ITAR while another admin has the wizard open). Snap back to
  // 'basic' rather than rendering an unknown step.
  useEffect(() => {
    if (!STEPS.find((s) => s.id === step)) setStep('basic');
  }, [STEPS, step]);

  const currentStepIndex = STEPS.findIndex(s => s.id === step);
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === STEPS.length - 1;

  const canProceed = useMemo(() => {
    if (step === 'basic') {
      return form.firstName.trim().length > 0
        && form.lastName.trim().length > 0
        && form.email.includes('@')
        && form.employeeId.trim().length > 0
        // B2b-T3 — Step 1 still requires a department decision; it is now
        // "all departments, or at least one", the same sentence the server
        // refuses `{ all: false, ids: [] }` with.
        && isCompleteScope(form.departments)
        && form.position.trim().length > 0
        && form.startDate
        // B2c-T2 — the admin must pick an employment type, and a
        // time-bounded engagement must say when it ends. Both facts come from
        // the served option, not from a literal compared against its key.
        //
        // Review B2cT2-BF-08 — `isAvailable` is stated, not implied. It is
        // already true that `employmentType` cannot leave `null` until the
        // options arrive, but the end-date RULE below reads `false` for an
        // absent answer, and a term that only works because of a neighbouring
        // term is how the permissive direction gets back in.
        && fieldOptions.isAvailable
        && form.employmentType !== null
        && (!fieldOptions.requiresEndDate(form.employmentType) || !!form.endDate);
    }
    if (step === 'clearance') {
      // Anything above the unclassified baseline requires US-Person verification
      // + a verifying officer (22 CFR 120.15 + DoD clearance grant procedure).
      if (clearanceRequiresUsPerson(form.clearance)) {
        if (form.usPersonStatus !== 'yes') return false;
        if (!form.usPersonVerifyingOfficer.trim()) return false;
      }
      return true;
    }
    if (step === 'access') {
      return form.roleIds.length > 0;
    }
    if (step === 'invitation') {
      return true;
    }
    return false;
  }, [step, form, fieldOptions]);

  const handleNext = () => {
    if (!canProceed) return;
    if (isLast) {
      void handleSubmit();
      return;
    }
    setStep(STEPS[currentStepIndex + 1].id);
  };

  const handleBack = () => {
    if (isFirst) return;
    setStep(STEPS[currentStepIndex - 1].id);
  };

  const handleReset = () => {
    setStep('basic');
    setForm(emptyState());
    setServerError(null);
    onOpenChange(false);
  };

  /**
   * Local wrapper around the exported pure helper. The helper takes
   * everything as plain inputs so it can be unit-tested without a
   * React tree.
   */
  function buildApiBody(): CreateUserBody {
    return buildCreateUserBodyFromForm(form, {
      clearanceVisible: policy.isVisible('clearance'),
      usPersonVisible: policy.isVisible('usPersonVerified'),
      hipaaVisible: policy.isVisible('hipaaBaaStatus'),
    });
  }

  /** Fired from the wizard's last-step "Create" button. */
  const handleSubmit = async () => {
    setServerError(null);
    const body = buildApiBody();
    try {
      const result = await createUser.mutateAsync(body);

      // Closes #256 (security): the raw invitation URL + Copy link
      // belong ONLY to the `manual_link` flow, where the admin
      // explicitly chose to distribute the URL themselves. The
      // server now returns `invitation: null` for `send_now`, so
      // the toast for that mode is a clean confirmation only.
      // Round 7 QA found the prior code displayed the one-time
      // token URL in the success toast for `send_now` too, which:
      //   - leaked the token into screen captures, browser history,
      //     screen-reader transcripts, browser extensions
      //   - duplicated a delivery channel the operator didn't ask
      //     for (the email-side delivery was already arranged)
      //   - broke the user expectation that "send invitation email"
      //     means the email is the channel, not the dashboard
      if (form.inviteMode === 'send_now') {
        toast.success('Invitation sent', {
          description: `An invitation email is being sent to ${body.email}.`,
        });
      } else if (result.invitation?.inviteUrl) {
        // manual_link mode — admin requested the copy-paste URL.
        toast.success('User created — copy the invitation link', {
          description: result.invitation.inviteUrl,
          duration: 12_000,
          action: {
            label: 'Copy link',
            onClick: () => {
              navigator.clipboard.writeText(result.invitation!.inviteUrl);
              toast.message('Invitation link copied to clipboard');
            },
          },
        });
      } else {
        toast.success('User created');
      }
      onCreated(result.user.id);
      setStep('basic');
      setForm(emptyState());
    } catch (err) {
      // The api-client surfaces server errors via .data.error.{code, message}
      // and a top-level .status. Route them back to the right step + field.
      // For VALIDATION_ERROR, `getApiErrorMessage` returns the api-client's
      // enriched message (with per-field detail) rather than the raw
      // "Validation failed" string — see PR #327 / api-error.ts.
      const code = getApiErrorCode(err);
      const msg = getApiErrorMessage(err, 'Failed to create user');
      setServerError({ code, message: msg });

      switch (code) {
        case 'EMAIL_EXISTS':
          setStep('basic');
          toast.error('Email already in use', { description: msg });
          break;
        case 'FRAMEWORK_NOT_ACTIVE':
          setStep('clearance');
          toast.error('Compliance field rejected', { description: msg });
          break;
        case 'VALIDATION_ERROR':
          toast.error('Validation failed', { description: msg });
          break;
        case 'FORBIDDEN':
          toast.error('Permission denied', { description: msg });
          break;
        case 'INVALID_DEPARTMENT':
          setStep('basic');
          toast.error('Department not in this tenant', { description: msg });
          break;
        case 'INVALID_MANAGER':
          setStep('basic');
          toast.error('Manager not in this tenant', { description: msg });
          break;
        case 'EMPLOYEE_ID_EXISTS':
          setStep('basic');
          toast.error('Employee ID already in use', { description: msg });
          break;
        default:
          toast.error('Could not create user', { description: msg });
      }
    }
  };

  const toggleMulti = <T extends string>(field: keyof FormState, value: T) => {
    setForm(prev => {
      const arr = prev[field] as T[];
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleReset()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Invite a new user
          </DialogTitle>
          <DialogDescription>
            Set up identity, clearance, access, and send the invitation.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const done = idx < currentStepIndex;
              const active = idx === currentStepIndex;
              return (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <div className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors min-w-0',
                    active && 'bg-primary text-primary-foreground',
                    done && 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
                    !active && !done && 'text-muted-foreground',
                  )}>
                    {done ? <Check className="h-3.5 w-3.5 shrink-0" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{s.label}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={cn('flex-1 h-[1px]', done ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-border')} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5">
            {step === 'basic' && <BasicStep form={form} setForm={setForm} />}
            {step === 'clearance' && <ClearanceStep form={form} setForm={setForm} />}
            {step === 'access' && <AccessStep form={form} setForm={setForm} toggleMulti={toggleMulti} />}
            {step === 'invitation' && <InvitationStep form={form} setForm={setForm} />}

            {/* Server error surface (rendered after the active step). The
                handleSubmit handler also routes the user back to the most
                relevant step on EMAIL_EXISTS / FRAMEWORK_NOT_ACTIVE. */}
            {serverError && (
              <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/5 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold text-red-900 dark:text-red-200">
                    {serverError.code}
                  </p>
                  <p className="text-red-800 dark:text-red-300 mt-0.5">
                    {serverError.message}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-muted-foreground">
              Step {currentStepIndex + 1} of {STEPS.length} — {STEPS[currentStepIndex].description}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleReset} disabled={createUser.isPending}>Cancel</Button>
              {!isFirst && (
                <Button variant="outline" onClick={handleBack} disabled={createUser.isPending}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              <Button onClick={handleNext} disabled={!canProceed || createUser.isPending}>
                {isLast ? (
                  form.inviteMode === 'send_now' ? (
                    <><Send className="h-4 w-4 mr-1" /> Create & Invite</>
                  ) : (
                    <><UserPlus className="h-4 w-4 mr-1" /> Create User</>
                  )
                ) : (
                  <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Step 1: Basic Info ──────────────────────────────────────────────────────

function BasicStep({ form, setForm }: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  // Closes #250 round 7 (defensive): use the functional updater so a
  // burst of field changes (e.g. typing in two date inputs in quick
  // succession) can't drop a key against a stale `form` closure. The
  // round-6 closure version did `setForm({ ...form, ...})`, which
  // captured the form value at BasicStep's most recent render and
  // could lose the second update if React hadn't flushed the first
  // re-render in between.
  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  // B2c-T2 — the employment-type options and their end-date rule, served by
  // GET /users/field-options. React Query dedupes this with the parent
  // dialog's own call.
  //
  // Review B2cT2-BF-03 — `isLoading`, `error` and `refetch` are READ here.
  // Step 1 cannot be completed without an employment type and the only control
  // is the served list, so an unanswered request is a dead wizard; the notice
  // below is the whole difference between "blocked with no message" and
  // "blocked, told why, with a Retry".
  const {
    options: fieldOptions,
    isLoading: fieldOptionsLoading,
    error: fieldOptionsError,
    refetch: refetchFieldOptions,
  } = useUserFieldOptions();
  // Review B2cT2-BF-08 — the rule is only believed once the options are in
  // hand. `requiresEndDate` answers `false` for an ABSENT answer, which is the
  // permissive direction.
  const endDateRequired =
    fieldOptions.isAvailable && fieldOptions.requiresEndDate(form.employmentType);

  // Pull live departments from the same source the Departments admin tab
  // and the user list filter use, so picking a department here is
  // consistent with what the admin sees on the Departments page.
  // B2b-T3 — EVERY department, because this picker is a WRITE path: a
  // department it cannot show is one an admin cannot choose. Review
  // BF-B2bT3-08 is why it pages rather than asking for `pageSize: 200` — that
  // was the API's maximum, so a tenant with more had departments nobody could
  // assign anyone to from this rail.
  const {
    departments: liveDepartments,
    isLoading: deptsLoading,
    truncated: deptsTruncated,
  } = useAllDepartments();
  // REVIEW BF-B2bT3-04 — NO MOCK FALLBACK. The wizard used to fall back to
  // MOCK_DEPARTMENTS (`dept-engineering`, …) whenever this list came back
  // empty — including a transient failure in a tenant that HAS departments —
  // and those ids were then dropped on the way to the server, which turned the
  // admin's narrow scope into the widest one. A tenant with no departments
  // configured shows the picker's own empty hint and the admin ticks "All
  // departments", which is the honest answer.
  const departmentOptions = liveDepartments.map((d) => ({ id: d.id, name: d.name }));
  const noDepartmentsConfigured = !deptsLoading && liveDepartments.length === 0;

  // Live tenant manager picker — admin/owner/power_user, active.
  // Replaces the four hardcoded mock UUIDs (`user-sarah-chen`, etc.).
  //
  // Closes #135 (wizard side): match on the canonical `roleName` field
  // (which the adapter sets to `api.role`) rather than only the
  // prefix-stripped `roleId`. Keeps the prefix-stripping fallback so
  // future format drifts don't break the picker.
  const { users: tenantUsers } = useUsers({ pageSize: 200 });
  const managerOptions = tenantUsers.filter((u) => {
    if (u.status !== 'active') return false;
    const apiRole = (u.roles[0]?.roleName ?? '').toLowerCase();
    if (apiRole === 'admin' || apiRole === 'owner' || apiRole === 'power_user') {
      return true;
    }
    return u.roles.some((r) => {
      // Strip both legacy `role-` and canonical `system:` prefixes
      // (closes F-01 round 8: adapter now emits `system:`).
      const stripped = (r.roleId ?? '')
        .replace(/^role-/, '')
        .replace(/^system:/, '')
        .toLowerCase();
      return stripped === 'admin' || stripped === 'owner' || stripped === 'power_user';
    });
  });

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">Identity</h4>
        <p className="text-xs text-muted-foreground">Basic personal information.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField label="First name" required>
          <Input value={form.firstName} onChange={(e) => update('firstName', e.target.value)} className="h-9 text-xs" />
        </FormField>
        <FormField label="Last name" required>
          <Input value={form.lastName} onChange={(e) => update('lastName', e.target.value)} className="h-9 text-xs" />
        </FormField>
        <FormField label="Email address" required>
          <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="h-9 text-xs" placeholder="name@acme.com" />
        </FormField>
        <FormField label="Employee ID" required>
          <Input value={form.employeeId} onChange={(e) => update('employeeId', e.target.value)} className="h-9 text-xs" placeholder="EMP-0000" />
        </FormField>
        <FormField label="Phone number">
          <Input value={form.phoneNumber} onChange={(e) => update('phoneNumber', e.target.value)} className="h-9 text-xs" placeholder="+1 555-0000" />
        </FormField>
      </div>

      <Separator />

      <div>
        <h4 className="text-sm font-semibold mb-1">Employment</h4>
        <p className="text-xs text-muted-foreground">Position and reporting structure.</p>
      </div>

      {/*
        B2c-T2 — THE OPTIONS COME FROM THE SERVER.
        This RadioGroup mapped over the literal `['fte', 'contractor',
        'vendor', 'partner']` and labelled each key by capitalising it. Two of
        those four values could not be stored, so the submit path carried a
        map that turned "Vendor" into `contractor`. The options and their
        labels are now GET /users/field-options' answer, and the value on each
        radio IS the value the store holds. Same control, same grid, same
        place on the step.
      */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold">Employment type</Label>
        {/* Review B2cT2-R-02 / BF-03 — the absence, named. */}
        <FieldOptionsNotice
          isAvailable={fieldOptions.isAvailable}
          isLoading={fieldOptionsLoading}
          error={fieldOptionsError}
          onRetry={refetchFieldOptions}
        />
        <RadioGroup
          value={form.employmentType ?? ''}
          onValueChange={(v) => update('employmentType', v as EmploymentType)}
          className="grid grid-cols-4 gap-2"
        >
          {fieldOptions.employmentTypes.map(t => (
            <label
              key={t.value}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors',
                form.employmentType === t.value ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
              )}
            >
              <RadioGroupItem value={t.value} />
              <span className="text-xs">{t.label}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* B2b-T3 — the SAME Department field in the SAME step and the same
            two-column row, picking a list instead of one value. */}
        <FormField label="Department" required>
          <DepartmentScopePicker
            value={form.departments}
            onChange={(next) => update('departments', next)}
            departments={departmentOptions}
            emptyHint={deptsLoading ? 'Loading…' : 'No departments configured for this tenant.'}
            aria-label="Departments"
          />
          {noDepartmentsConfigured && (
            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
              No departments configured for this tenant yet — choose “All departments”.
            </p>
          )}
          {deptsTruncated && (
            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
              Showing the first departments only — this tenant has more than the picker can load.
            </p>
          )}
        </FormField>
        <FormField label="Position / duty title" required>
          <Input value={form.position} onChange={(e) => update('position', e.target.value)} className="h-9 text-xs" placeholder="e.g., Maintenance Technician" />
        </FormField>
      </div>

      <FormField label="Reports to (manager)">
        <Select value={form.managerId || 'none'} onValueChange={(v) => update('managerId', v === 'none' ? '' : v)}>
          <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Auto-assign from department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-xs">Auto-assign from department</SelectItem>
            {managerOptions.length === 0 ? (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                No eligible managers in this tenant yet.
              </div>
            ) : (
              managerOptions.map((u) => (
                <SelectItem key={u.id} value={u.id} className="text-xs">
                  {u.fullName || u.email}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FormField>

      <div className="grid grid-cols-2 gap-3">
        {/*
         * Closes #250 round 8: DateInput wraps `<input type="date">` with
         * an onBlur fallback that re-syncs form state from the DOM value.
         * Native `<input type="date">` does not reliably fire React's
         * onChange for picker/keyboard transitions in every browser +
         * automation combination, which is why three rounds of helper
         * fixes did not close this bug. See date-input.tsx for the full
         * write-up of the root cause.
         */}
        <FormField label="Start date" required>
          <DateInput
            value={form.startDate}
            onChange={(v) => update('startDate', v)}
            className="h-9 text-xs"
          />
        </FormField>
        {/*
          B2c-T2 — the field asks the SERVED OPTION whether this engagement
          ends, instead of comparing the key against `'fte'` three times.
        */}
        <FormField
          label={`End date${endDateRequired ? '' : ' (optional)'}`}
          required={endDateRequired}
        >
          <DateInput
            value={form.endDate}
            onChange={(v) => update('endDate', v)}
            className="h-9 text-xs"
            min={form.startDate}
          />
          {endDateRequired && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Required for a time-bounded engagement — the account
              auto-deactivates on this date.
            </p>
          )}
        </FormField>
      </div>

      {/* Cost center — closes #183. Optional. Free-text HR identifier;
          mirrors the field on the Edit User dialog so values entered
          here round-trip through detail / edit without surprise. */}
      <FormField label="Cost center (optional)">
        <Input
          value={form.costCenter}
          onChange={(e) => update('costCenter', e.target.value)}
          placeholder="e.g. FINANCE-001"
          maxLength={100}
          className="h-9 text-xs"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          HR cost-center identifier. Used for reporting / chargeback.
        </p>
      </FormField>
    </div>
  );
}

// ─── Step 2: Clearance & Classification ─────────────────────────────────────

function ClearanceStep({
  form, setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  // Use the canonical helper from @argusai/shared so the rule is single-sourced.
  const requiresUsPerson = clearanceRequiresUsPerson(form.clearance);

  // Framework-driven gating: clearance / US-Person / HIPAA fields are
  // only persisted when the tenant has the matching compliance framework
  // active. Without one, the API drops the values silently. Surface this
  // to the admin so they don't think they're configuring something the
  // server is going to keep.
  const { policy } = useIdentityFieldPolicy();

  return (
    <div className="space-y-5">
      {!policy.hasAny && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              No compliance framework active for this tenant
            </p>
            <p className="text-amber-800 dark:text-amber-300 mt-0.5">
              Clearance, US-Person verification, and HIPAA fields will not
              be recorded for this user. The defaults shown below
              (<code>unclassified</code>, unverified, no PHI access) describe
              what the API records. Activate a framework under
              Admin → Compliance to start tracking these fields.
            </p>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold mb-1">Security clearance</h4>
        <p className="text-xs text-muted-foreground">
          Per NIST and DoD guidance. Clearance must be granted by a security officer before the user can access classified content.
        </p>
      </div>

      <FormField label="Clearance level">
        <Select value={form.clearance} onValueChange={(v) => update('clearance', v as ClearanceLevel)}>
          <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unclassified" className="text-xs">Unclassified</SelectItem>
            <SelectItem value="cui" className="text-xs">CUI</SelectItem>
            <SelectItem value="secret" className="text-xs">Secret</SelectItem>
            <SelectItem value="ts" className="text-xs">Top Secret</SelectItem>
            <SelectItem value="ts_sci" className="text-xs">TS/SCI</SelectItem>
          </SelectContent>
        </Select>
      </FormField>

      <Separator />

      <div>
        <h4 className="text-sm font-semibold mb-1">US Person verification</h4>
        <p className="text-xs text-muted-foreground">
          Required by ITAR §120.15 before the user can access ITAR-controlled content.
          A security officer must verify the listed document before setting to "Verified."
        </p>
      </div>

      <FormField label="US Person status" required={requiresUsPerson}>
        <RadioGroup
          value={form.usPersonStatus}
          onValueChange={(v) => update('usPersonStatus', v as FormState['usPersonStatus'])}
          className="space-y-2"
        >
          <RadioRow value="yes" title="Verified — US Person"
            description="US citizen or lawful permanent resident; verification document reviewed." />
          <RadioRow value="no" title="Non-US Person"
            description="Not eligible for ITAR-controlled content access." />
          <RadioRow value="unknown" title="Pending verification"
            description="Will be set after user onboarding." />
        </RadioGroup>
      </FormField>

      {form.usPersonStatus === 'yes' && (
        <div className="rounded-md border bg-muted/20 p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Verification document">
              <Select value={form.usPersonDocType} onValueChange={(v) => update('usPersonDocType', v as UsPersonDocumentType)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="us_passport" className="text-xs">US Passport</SelectItem>
                  <SelectItem value="drivers_license_ssn" className="text-xs">Driver's License + SSN</SelectItem>
                  <SelectItem value="permanent_resident_card" className="text-xs">Permanent Resident Card</SelectItem>
                  <SelectItem value="i_551" className="text-xs">I-551</SelectItem>
                  <SelectItem value="birth_certificate" className="text-xs">Birth Certificate</SelectItem>
                  <SelectItem value="naturalization" className="text-xs">Naturalization Certificate</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Verifying officer" required>
              <Input
                value={form.usPersonVerifyingOfficer}
                onChange={(e) => update('usPersonVerifyingOfficer', e.target.value)}
                className="h-9 text-xs"
                placeholder="Security officer name"
              />
            </FormField>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Verification record is logged in the audit trail per ITAR §120.15. Recorded at:
            <code className="ml-1 bg-muted px-1 py-0.5 rounded">{new Date().toLocaleString()}</code>
          </p>
        </div>
      )}

      <Separator />

      <div>
        <h4 className="text-sm font-semibold mb-1">Healthcare (HIPAA)</h4>
        <p className="text-xs text-muted-foreground">
          Only set for users who will access Protected Health Information.
        </p>
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox
          checked={form.hipaaWorkforceMember}
          onCheckedChange={(c) => update('hipaaWorkforceMember', c === true)}
          className="mt-0.5"
        />
        <div>
          <div className="text-xs font-medium">HIPAA workforce member</div>
          <div className="text-[10px] text-muted-foreground">
            User has signed HIPAA workforce agreement and completed HIPAA training.
          </div>
        </div>
      </label>
    </div>
  );
}

// ─── Step 3: Access (roles + groups) ────────────────────────────────────────

function AccessStep({
  form, toggleMulti,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  toggleMulti: <T extends string>(field: keyof FormState, value: T) => void;
}) {
  // Wire to the SAME source the Users / Roles tab uses so member counts
  // shown here cannot drift. Falls back to the mock catalog when the API
  // returns empty (fresh tenant) — explicitly labelled in copy below.
  const { roles: realRoles, isLoading: rolesLoading } = useRoles();
  const { groups: realGroups, isLoading: groupsLoading } = useUserGroups({ pageSize: 100 });
  const ROLES = realRoles.length > 0
    ? realRoles
        // A retired role grants nothing; offering it would create a user with
        // no authority and no way to see why.
        .filter((r) => r.retiredAt === null)
        .map((r) => ({
          // The KEY, not the row id: `POST /users` takes a role key, and the
          // wizard must not have to translate a UUID back into one.
          id: r.key,
          name: r.name,
          description: r.description ?? '',
          isSystem: r.kind === 'platform',
          userCount: r.assignedUserCount,
        }))
    : MOCK_ROLES.map((r) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        isSystem: r.isSystem,
        userCount: r.userCount,
      }));
  const GROUPS = realGroups.length > 0
    ? realGroups.map((g) => ({
        id: g.id,
        name: g.name,
        description: g.description ?? '',
        type: (g.membershipType === 'dynamic' ? 'dynamic' : 'user_group') as 'dynamic' | 'user_group' | 'security',
        memberCount: g.memberCount,
      }))
    : MOCK_GROUPS.map((g) => ({
        id: g.id,
        name: g.name,
        description: g.description,
        type: g.type as 'dynamic' | 'user_group' | 'security',
        memberCount: g.memberCount,
      }));
  const usingMockRoles = realRoles.length === 0 && !rolesLoading;
  const usingMockGroups = realGroups.length === 0 && !groupsLoading;

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">Roles</h4>
        <p className="text-xs text-muted-foreground">
          Direct role assignments. Additional roles may be inherited from department or group membership.
          {usingMockRoles && (
            <span className="block mt-0.5 text-amber-600 dark:text-amber-400">
              Showing reference role catalog — your tenant has no custom roles yet.
            </span>
          )}
        </p>
      </div>

      <div className="space-y-2">
        {ROLES.map(r => (
          <label
            key={r.id}
            className={cn(
              'flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors',
              form.roleIds.includes(r.id) ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
            )}
          >
            <Checkbox
              checked={form.roleIds.includes(r.id)}
              onCheckedChange={() => toggleMulti('roleIds', r.id)}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium">{r.name}</span>
                {r.isSystem && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">System</Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{r.description}</p>
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {r.userCount} users
            </span>
          </label>
        ))}
      </div>

      <Separator />

      <div>
        <h4 className="text-sm font-semibold mb-1">Group memberships</h4>
        <p className="text-xs text-muted-foreground">
          Groups bundle roles and resource access. Dynamic groups auto-assign based on user attributes.
          {usingMockGroups && (
            <span className="block mt-0.5 text-amber-600 dark:text-amber-400">
              Showing reference group catalog — your tenant has no user groups yet.
            </span>
          )}
        </p>
      </div>

      <div className="space-y-2">
        {GROUPS.map(g => (
          <label
            key={g.id}
            className={cn(
              'flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors',
              form.groupIds.includes(g.id) ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
              g.type === 'dynamic' && 'opacity-70',
            )}
          >
            <Checkbox
              checked={form.groupIds.includes(g.id)}
              onCheckedChange={() => toggleMulti('groupIds', g.id)}
              disabled={g.type === 'dynamic'}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-medium">{g.name}</span>
                <Badge variant="outline" className={cn(
                  'text-[9px] px-1 py-0 h-4 capitalize',
                  g.type === 'security' && 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
                  g.type === 'user_group' && 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
                  g.type === 'dynamic' && 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
                )}>
                  {g.type.replace('_', ' ')}
                </Badge>
                {g.type === 'dynamic' && (
                  <span className="text-[9px] text-muted-foreground">Auto-assigned</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{g.description}</p>
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {g.memberCount} members
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Step 4: Invitation ──────────────────────────────────────────────────────

function InvitationStep({
  form, setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  // Resolve role/group/department display names from BOTH the live catalog
  // (preferred) and the mock catalog (fallback) so we never render a raw
  // UUID and the review screen agrees with what the admin pages show.
  const { roles: realRoles } = useRoles();
  const { groups: realGroups } = useUserGroups({ pageSize: 100 });
  const { departments: realDepartments } = useAllDepartments();
  // B2c-T2 — the employment-type LABEL for the review row, from the same
  // served list the picker on step 1 renders.
  const { options: reviewFieldOptions } = useUserFieldOptions();
  // By KEY, the way the selector now identifies a role (B1-T4); by id too, so
  // form state seeded elsewhere still renders a name instead of a raw UUID.
  const resolveRoleName = (id: string) =>
    realRoles.find((r) => r.key === id)?.name
    ?? realRoles.find((r) => r.id === id)?.name
    ?? MOCK_ROLES.find((r) => r.id === id)?.name
    ?? id;
  const resolveGroupName = (id: string) =>
    realGroups.find((g) => g.id === id)?.name ?? MOCK_GROUPS.find((g) => g.id === id)?.name ?? id;
  // Review BF-B2bT3-04 — no mock fallback here either: the review screen used
  // to print a reference department's NAME for an id the server was never going
  // to accept, so the last thing the admin read before pressing Create was a
  // scope that did not exist.
  const resolveDepartmentName = (id: string) =>
    realDepartments.find((d) => d.id === id)?.name ?? id;
  const selectedRoles = form.roleIds.map(resolveRoleName);
  const selectedGroups = form.groupIds.map(resolveGroupName);

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">Review</h4>
        <p className="text-xs text-muted-foreground">Summary of the new user's configuration.</p>
      </div>

      <div className="rounded-md border bg-muted/20 p-4 space-y-2 text-xs">
        <SummaryRow label="Name" value={`${form.firstName} ${form.lastName}`} />
        <SummaryRow label="Email" value={form.email} />
        <SummaryRow label="Employee ID" value={form.employeeId} />
        {/* B2b-T3 — the list, or "All departments", through the one helper the
            roster cell and the detail page use. */}
        <SummaryRow
          label="Department"
          value={formatUserDepartments({
            all: form.departments.all,
            items: form.departments.ids.map((id) => ({ id, name: resolveDepartmentName(id) })),
          })}
        />
        <SummaryRow label="Position" value={form.position} />
        {/* B2c-T2 — the served label, not a key with one special case. */}
        <SummaryRow label="Employment" value={reviewFieldOptions.labelFor(form.employmentType) ?? '—'} />
        {/*
         * Closes #250: surface Start Date and End Date on the Review
         * step so the admin can confirm the values before submit.
         * Previously these were captured in Step 1 but invisible at
         * Review, so a wrong default (or a missed override) could not
         * be caught by the admin and the misvalue silently persisted.
         */}
        <SummaryRow label="Start date" value={formatDateOnly(form.startDate)} />
        {form.endDate && (
          <SummaryRow label="End date" value={formatDateOnly(form.endDate)} />
        )}
        {form.costCenter.trim() && (
          <SummaryRow label="Cost center" value={form.costCenter.trim()} />
        )}
        <SummaryRow label="Clearance" value={CLEARANCE_LABELS[form.clearance]} />
        <SummaryRow label="US Person" value={
          form.usPersonStatus === 'yes' ? `Verified (${form.usPersonDocType.replace(/_/g, ' ')})` :
          form.usPersonStatus === 'no' ? 'Non-US Person' :
          'Pending'
        } />
        {form.hipaaWorkforceMember && (
          <SummaryRow label="HIPAA" value="Workforce member (BAA pending)" />
        )}
        <SummaryRow label="Roles" value={selectedRoles.join(', ') || '—'} />
        {selectedGroups.length > 0 && <SummaryRow label="Groups" value={selectedGroups.join(', ')} />}
      </div>

      <Separator />

      <div>
        <h4 className="text-sm font-semibold mb-1">How do you want to onboard this user?</h4>
      </div>

      <RadioGroup
        value={form.inviteMode}
        onValueChange={(v) => update('inviteMode', v as FormState['inviteMode'])}
        className="space-y-2"
      >
        <RadioRow
          value="send_now"
          title="Send invitation email now"
          description="User receives an email with a one-time setup link, valid for 7 days."
          icon={Send}
        />
        <RadioRow
          value="generate_link"
          title="Generate a copy-paste invitation link"
          description="Dialog shows the link after creation. Valid for 7 days. You share it manually."
          icon={Link2}
        />
      </RadioGroup>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function FormField({
  label, required, children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

function RadioRow({
  value, title, description, icon: Icon,
}: {
  value: string;
  title: string;
  description: string;
  icon?: any;
}) {
  return (
    <label className="flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors hover:bg-muted/50 has-[:checked]:bg-primary/5 has-[:checked]:border-primary/30">
      <RadioGroupItem value={value} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-xs font-medium">{title}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
      </div>
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-28 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground pt-0.5">{label}</span>
      <span className="flex-1 text-xs">{value}</span>
    </div>
  );
}
